import { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { type ViewField, type ViewFieldListResponse, ViewFieldSupport } from '@air/api/types';

import { setConfigurableViewFieldsAction } from '~/store/configViews/actions';
import { filterOutInternalAnalyticsFields } from '~/swr-hooks/gallery/viewFields/filterOutInternalAnalyticsFields';

export const SHOWCASE_ALLOWED_SORT_FIELDS = new Set<ViewField['name']>([
  'dateModified',
  'dateCreated',
  'dateUploaded',
  'name',
]);

const filterShowcaseSortFields = (fields: ViewField[], allowedSortFields: Set<ViewField['name']>): ViewField[] =>
  fields.map((field) => {
    if (field.supports.includes(ViewFieldSupport.sort) && !allowedSortFields.has(field.name)) {
      return {
        ...field,
        supports: field.supports.filter((support) => support !== ViewFieldSupport.sort),
      };
    }

    return field;
  });

export type UseFilteredConfigViewFieldsParams = {
  allowedSortFields: Set<ViewField['name']>;
  configViewOptions: ViewFieldListResponse | undefined;
};

export const useFilteredConfigViewFields = ({
  allowedSortFields,
  configViewOptions,
}: UseFilteredConfigViewFieldsParams) => {
  const dispatch = useDispatch();

  // Showcase tables load through the public list, which never fetches metrics.
  const filteredConfigViewFields = useMemo(
    () =>
      configViewOptions
        ? filterShowcaseSortFields(filterOutInternalAnalyticsFields(configViewOptions).data, allowedSortFields)
        : undefined,
    [allowedSortFields, configViewOptions],
  );

  useEffect(() => {
    if (filteredConfigViewFields) {
      dispatch(setConfigurableViewFieldsAction({ configurableViewFields: filteredConfigViewFields }));
    }
  }, [filteredConfigViewFields, dispatch]);
};
